import { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import { NETWORK_CONFIG, APP_CONFIG } from '../config';
import { ALPHAMETA_ABI, ERC20_ABI } from '../abi';

export function useWallet() {
  const [account, setAccount] = useState(null);
  const [userId, setUserId] = useState(null);
  const [uplineId, setUplineId] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isCorrectNetwork, setIsCorrectNetwork] = useState(false);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [contract, setContract] = useState(null);
  const [tokenContract, setTokenContract] = useState(null);
  const [userData, setUserData] = useState(null);
  const [userInvestments, setUserInvestments] = useState([]);
  const [userActivities, setUserActivities] = useState([]);
  const [totalActivityCount, setTotalActivityCount] = useState(0);
  const [contractStats, setContractStats] = useState(null);
  const [tokenBalance, setTokenBalance] = useState('0');
  const [error, setError] = useState(null);
  const [isRegistered, setIsRegistered] = useState(false);
  
  const mountedRef = useRef(true);
  const initializingRef = useRef(false);

  const getEthereum = () => {
    if (typeof window !== 'undefined') {
      return window.ethereum || window.BinanceChain || null;
    }
    return null;
  };

  const switchNetwork = async () => {
    const ethereum = getEthereum();
    if (!ethereum) return false;

    try {
      await ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: NETWORK_CONFIG.chainIdHex }]
      });
      return true;
    } catch (switchError) {
      if (switchError.code === 4902) {
        try {
          await ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: NETWORK_CONFIG.chainIdHex,
              chainName: NETWORK_CONFIG.chainName,
              rpcUrls: NETWORK_CONFIG.rpcUrls,
              blockExplorerUrls: NETWORK_CONFIG.blockExplorerUrls,
              nativeCurrency: NETWORK_CONFIG.nativeCurrency
            }]
          });
          return true;
        } catch (addError) {
          console.error('Failed to add network:', addError);
          return false;
        }
      }
      return false;
    }
  };

  const checkNetwork = async (ethereum) => {
    try {
      const chainId = await ethereum.request({ method: 'eth_chainId' });
      const isCorrect = chainId === NETWORK_CONFIG.chainIdHex;
      if (mountedRef.current) {
        setIsCorrectNetwork(isCorrect);
      }
      return isCorrect;
    } catch (err) {
      return false;
    }
  };

  const initializeContracts = async (signerInstance) => {
    try {
      const alphaContract = new ethers.Contract(
        NETWORK_CONFIG.contractAddress,
        ALPHAMETA_ABI,
        signerInstance
      );
      
      const tokenContractInstance = new ethers.Contract(
        NETWORK_CONFIG.tokenAddress,
        ERC20_ABI,
        signerInstance
      );

      if (mountedRef.current) {
        setContract(alphaContract);
        setTokenContract(tokenContractInstance);
      }
      
      return { alphaContract, tokenContractInstance };
    } catch (err) {
      console.error('Failed to initialize contracts:', err);
      return { alphaContract: null, tokenContractInstance: null };
    }
  };

  const fetchUserData = async (contractInstance, userAddress) => {
    if (!contractInstance || !userAddress) return;
    
    try {
      const id = await contractInstance.addressToId(userAddress);
      const userIdNum = id.toNumber();
      
      if (mountedRef.current) {
        setUserId(userIdNum);
        setIsRegistered(userIdNum > 0);
      }
      
      if (userIdNum > 0) {
        const [basicInfo, extendedInfo, activities, clubInfo, userInfo] = await Promise.all([
          contractInstance.getUserBasicInfo(userIdNum),
          contractInstance.getUserExtendedInfo(userIdNum),
          contractInstance.getUserActivities(userIdNum, 0, 10),
          contractInstance.getUserClubInfo(userIdNum),
          contractInstance.users(userIdNum)
        ]);

        // Fetch investments separately to handle old/new contract versions
        let formattedInvestments = [];
        try {
          const investments = await contractInstance.getUserInvestmentDetails(userIdNum);
          
          // Check if lastTradeStart exists (new contract)
          const hasLastTradeStart = investments.lastTradeStart !== undefined;
          
          formattedInvestments = investments.amounts.map((amount, i) => ({
            amount: ethers.utils.formatEther(amount),
            timestamp: investments.timestamps[i].toNumber(),
            profitRate: investments.roiRates[i].toNumber() / 100,
            profitEarned: ethers.utils.formatEther(investments.roiEarned[i]),
            otherIncomeEarned: ethers.utils.formatEther(investments.otherIncomeEarned[i]),
            lastTradeStart: hasLastTradeStart ? investments.lastTradeStart[i].toNumber() : 0,
            active: investments.active[i],
            tradeActive: investments.tradeActive[i],
            investmentType: investments.investmentTypes[i]
          }));
        } catch (invError) {
          console.error('Error fetching investments (may be old contract):', invError);
          // Try old contract format without lastTradeStart
          try {
            const oldContract = new ethers.Contract(
              NETWORK_CONFIG.contractAddress,
              [
                "function getUserInvestmentDetails(uint256 userId) view returns (uint256[] amounts, uint256[] timestamps, uint256[] roiRates, uint256[] roiEarned, uint256[] otherIncomeEarned, bool[] active, bool[] tradeActive, uint8[] investmentTypes)"
              ],
              contractInstance.signer
            );
            const investments = await oldContract.getUserInvestmentDetails(userIdNum);
            
            formattedInvestments = investments.amounts.map((amount, i) => ({
              amount: ethers.utils.formatEther(amount),
              timestamp: investments.timestamps[i].toNumber(),
              profitRate: investments.roiRates[i].toNumber() / 100,
              profitEarned: ethers.utils.formatEther(investments.roiEarned[i]),
              otherIncomeEarned: ethers.utils.formatEther(investments.otherIncomeEarned[i]),
              lastTradeStart: 0, // Old contract doesn't have this
              active: investments.active[i],
              tradeActive: investments.tradeActive[i],
              investmentType: investments.investmentTypes[i]
            }));
          } catch (oldError) {
            console.error('Error fetching investments (old format):', oldError);
          }
        }

        if (mountedRef.current) {
          setUplineId(userInfo.referrerId.toNumber());
          
          setUserData({
            wallet: basicInfo[0],
            totalInvestment: ethers.utils.formatEther(basicInfo[1]),
            totalEarned: ethers.utils.formatEther(basicInfo[2]),
            pendingRewards: ethers.utils.formatEther(basicInfo[3]),
            directReferrals: extendedInfo[0].toNumber(),
            teamVolume: ethers.utils.formatEther(extendedInfo[1]),
            directBusiness: ethers.utils.formatEther(extendedInfo[2]),
            isActive: extendedInfo[3],
            isBlacklisted: extendedInfo[4],
            clubLevel: clubInfo[0].toNumber(),
            clubClaimed: clubInfo[1]
          });

          setUserInvestments(formattedInvestments);

          const formattedActivities = activities[0].map(act => ({
            timestamp: act.timestamp.toNumber(),
            amount: ethers.utils.formatEther(act.amount),
            activityType: act.activityType
          }));
          setUserActivities(formattedActivities);
          setTotalActivityCount(activities[1].toNumber());
        }
      }
    } catch (err) {
      console.error('Failed to fetch user data:', err);
    }
  };

  const fetchActivities = async (page = 0, pageSize = 10) => {
    if (!contract || !userId) return { activities: [], total: 0 };
    
    try {
      const activities = await contract.getUserActivities(userId, page, pageSize);
      const formattedActivities = activities[0].map(act => ({
        timestamp: act.timestamp.toNumber(),
        amount: ethers.utils.formatEther(act.amount),
        activityType: act.activityType
      }));
      return { activities: formattedActivities, total: activities[1].toNumber() };
    } catch (err) {
      console.error('Failed to fetch activities:', err);
      return { activities: [], total: 0 };
    }
  };

  const fetchLevelUsers = async (level) => {
    if (!contract || !userId) return [];
    
    try {
      const levelCounts = await contract.getUserLevelCounts(userId);
      const count = levelCounts[level].toNumber();
      const users = [];
      
      for (let i = 0; i < Math.min(count, 50); i++) {
        try {
          const levelUserId = await contract.levelUsers(userId, level, i);
          users.push(levelUserId.toNumber());
        } catch (e) {
          break;
        }
      }
      return users;
    } catch (err) {
      console.error('Failed to fetch level users:', err);
      return [];
    }
  };

  const fetchContractStats = async (contractInstance) => {
    if (!contractInstance) return;
    
    try {
      const stats = await contractInstance.getContractStats();
      if (mountedRef.current) {
        setContractStats({
          totalUsers: stats[0].toNumber(),
          totalInvestment: ethers.utils.formatEther(stats[1]),
          activeUsers: stats[2].toNumber(),
          blacklistedUsers: stats[3].toNumber(),
          contractBalance: ethers.utils.formatEther(stats[4])
        });
      }
    } catch (err) {
      console.error('Failed to fetch contract stats:', err);
    }
  };

  const fetchTokenBalance = async (tokenContractInstance, userAddress) => {
    if (!tokenContractInstance || !userAddress) return;
    
    try {
      const balance = await tokenContractInstance.balanceOf(userAddress);
      if (mountedRef.current) {
        setTokenBalance(ethers.utils.formatEther(balance));
      }
    } catch (err) {
      console.error('Failed to fetch token balance:', err);
    }
  };

  const connectWallet = async () => {
    if (initializingRef.current) return;
    
    const ethereum = getEthereum();
    if (!ethereum) {
      setError('Please install a Web3 wallet like Trust Wallet or MetaMask');
      return;
    }

    initializingRef.current = true;
    setIsConnecting(true);
    setError(null);

    try {
      const accounts = await ethereum.request({ method: 'eth_requestAccounts' });
      
      if (!accounts || accounts.length === 0) {
        throw new Error('No accounts found');
      }

      let networkOk = await checkNetwork(ethereum);
      if (!networkOk) {
        networkOk = await switchNetwork();
        if (!networkOk) {
          throw new Error('Please switch to BNB Smart Chain');
        }
      }

      const web3Provider = new ethers.providers.Web3Provider(ethereum, 'any');
      const web3Signer = web3Provider.getSigner();
      
      if (mountedRef.current) {
        setProvider(web3Provider);
        setSigner(web3Signer);
        setAccount(accounts[0]);
        setIsCorrectNetwork(true);
      }

      const { alphaContract, tokenContractInstance } = await initializeContracts(web3Signer);
      
      if (alphaContract && tokenContractInstance) {
        await Promise.all([
          fetchUserData(alphaContract, accounts[0]),
          fetchContractStats(alphaContract),
          fetchTokenBalance(tokenContractInstance, accounts[0])
        ]);
      }

    } catch (err) {
      console.error('Connection error:', err);
      if (mountedRef.current) {
        setError(err.message || 'Failed to connect wallet');
      }
    } finally {
      initializingRef.current = false;
      if (mountedRef.current) {
        setIsConnecting(false);
      }
    }
  };

  const disconnectWallet = () => {
    setAccount(null);
    setUserId(null);
    setUplineId(null);
    setProvider(null);
    setSigner(null);
    setContract(null);
    setTokenContract(null);
    setUserData(null);
    setUserInvestments([]);
    setUserActivities([]);
    setTokenBalance('0');
    setIsCorrectNetwork(false);
    setIsRegistered(false);
  };

  const refreshData = async () => {
    if (contract && account) {
      await Promise.all([
        fetchUserData(contract, account),
        fetchContractStats(contract),
        tokenContract && fetchTokenBalance(tokenContract, account)
      ]);
    }
  };

  const registerAndInvest = async (referrerId, tokenAmount) => {
    if (!contract || !tokenContract) throw new Error('Not connected');
    
    const amount = ethers.utils.parseEther(tokenAmount.toString());
    
    const tx1 = await tokenContract.approve(NETWORK_CONFIG.contractAddress, amount);
    await tx1.wait();
    
    const tx2 = await contract.registerAndInvest(referrerId || APP_CONFIG.defaultReferrerId, amount);
    await tx2.wait();
    
    await refreshData();
    return tx2;
  };

  const invest = async (tokenAmount) => {
    if (!contract || !tokenContract) throw new Error('Not connected');
    
    const amount = ethers.utils.parseEther(tokenAmount.toString());
    
    const tx1 = await tokenContract.approve(NETWORK_CONFIG.contractAddress, amount);
    await tx1.wait();
    
    const tx2 = await contract.invest(amount);
    await tx2.wait();
    
    await refreshData();
    return tx2;
  };

  const startTrade = async (investmentIndex) => {
    if (!contract) throw new Error('Not connected');
    
    const tx = await contract.startTrade(investmentIndex);
    await tx.wait();
    
    await refreshData();
    return tx;
  };

  const claimTradeROI = async (investmentIndex) => {
    if (!contract) throw new Error('Not connected');
    
    const tx = await contract.claimTradeROI(investmentIndex);
    await tx.wait();
    
    await refreshData();
    return tx;
  };

  const claimRewards = async () => {
    if (!contract) throw new Error('Not connected');
    
    const tx = await contract.claimRewards();
    await tx.wait();
    
    await refreshData();
    return tx;
  };

  useEffect(() => {
    mountedRef.current = true;
    const ethereum = getEthereum();
    
    if (ethereum) {
      const handleAccountsChanged = (accounts) => {
        if (mountedRef.current) {
          if (accounts.length === 0) {
            disconnectWallet();
          } else if (accounts[0] !== account) {
            setAccount(accounts[0]);
            if (contract) {
              fetchUserData(contract, accounts[0]);
              if (tokenContract) {
                fetchTokenBalance(tokenContract, accounts[0]);
              }
            }
          }
        }
      };

      const handleChainChanged = async () => {
        if (mountedRef.current) {
          const isCorrect = await checkNetwork(ethereum);
          if (!isCorrect && account) {
            await switchNetwork();
          }
        }
      };

      ethereum.on('accountsChanged', handleAccountsChanged);
      ethereum.on('chainChanged', handleChainChanged);

      return () => {
        mountedRef.current = false;
        ethereum.removeListener('accountsChanged', handleAccountsChanged);
        ethereum.removeListener('chainChanged', handleChainChanged);
      };
    }

    return () => {
      mountedRef.current = false;
    };
  }, [account, contract, tokenContract]);

  return {
    account,
    userId,
    uplineId,
    isConnecting,
    isCorrectNetwork,
    isRegistered,
    provider,
    signer,
    contract,
    tokenContract,
    userData,
    userInvestments,
    userActivities,
    totalActivityCount,
    contractStats,
    tokenBalance,
    error,
    connectWallet,
    disconnectWallet,
    refreshData,
    switchNetwork,
    registerAndInvest,
    invest,
    startTrade,
    claimTradeROI,
    claimRewards,
    fetchActivities,
    fetchLevelUsers
  };
}
